import Modal from 'components/common/modal';
import ConfigurationZoneThumbnail from 'components/configuration-page/configuration-zone-thumbnail';
import { toggleSetValue } from 'components/equipment-page/equipment-filter-helpers';
import Checkbox from 'components/inputs/checkbox';
import { ConfigurationType } from 'data/types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  getConfigurationAirHandlerKinds,
  getConfigurationCondenserCount
} from 'utils/configuration-summary';

type ConfigurationSelectionModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  configurations: ConfigurationType[];
  onConfirm: (selectedConfigIds: string[]) => void;
}>;

export default function ConfigurationSelectionModal({
  openState,
  configurations,
  onConfirm
}: ConfigurationSelectionModalProps) {
  const [open] = openState;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelected(new Set(configurations.map((config) => config.uniqueId)));
    }
  }, [open, configurations]);

  return (
    <Modal
      openState={openState}
      title='Choose Configurations to Compare'
      buttonText='View Systems'
      size='wide'
      saveDisabled={selected.size === 0}
      onSave={() => onConfirm(Array.from(selected))}
    >
      <div className='flex flex-col gap-3'>
        <p className='text-sm text-gray-600'>
          Which of these configurations do you want to look at system options
          from? You can change this anytime on the next page.
        </p>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          {configurations.map((config) => {
            const checked = selected.has(config.uniqueId);
            const htmlId = `config-select-${config.uniqueId}`;
            const condenserCount = getConfigurationCondenserCount(config);
            const airHandlerKinds = getConfigurationAirHandlerKinds(config);
            return (
              <label
                key={config.uniqueId}
                htmlFor={htmlId}
                className='flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50'
              >
                <Checkbox
                  htmlId={htmlId}
                  value={checked}
                  onChange={() =>
                    setSelected((prev) => toggleSetValue(prev, config.uniqueId))
                  }
                />
                <div className='flex flex-1 flex-col gap-2'>
                  <span className='text-sm font-medium text-gray-900'>
                    {config.name || 'Configuration'}
                  </span>
                  <ConfigurationZoneThumbnail configuration={config} />
                  <div className='flex flex-col gap-0.5 text-xs text-gray-500'>
                    <span>
                      {condenserCount} condenser
                      {condenserCount === 1 ? '' : 's'}
                    </span>
                    <span>
                      {airHandlerKinds.length > 0
                        ? airHandlerKinds
                            .map((kind) => `${kind.count} ${kind.label}`)
                            .join(', ')
                        : 'No air handlers'}
                    </span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
